import { Building2, GraduationCap, MapPin, Users2 } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { EmptyState } from '@/components/ui/EmptyState'
import type { Breadcrumb } from '@/types/shared'

type School = {
  id: string
  name: string
  prefecture: string | null
  league: string | null
  selfIntroduction: string | null
  coachesCount: number
  iconUrl?: string | null
}

type SchoolsIndexProps = {
  schools: School[]
  breadcrumbs: Breadcrumb[]
}

export default function TeamSchoolsIndex({ schools, breadcrumbs }: SchoolsIndexProps) {
  return (
    <AppShell>
      <PageHeader
        title="登録高校一覧"
        description="Talentboxに登録されている高校の一覧です。"
        breadcrumbs={breadcrumbs}
      />

      {schools.length === 0 ? (
        <EmptyState
          icon={<GraduationCap className="h-12 w-12" />}
          title="登録されている高校がありません"
          description="現在、登録されている高校はありません。"
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {schools.map((school) => (
            <Card key={school.id} className="relative overflow-hidden border border-white/5 bg-white/5 p-6">
              <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-brand-500/10" />
              <div className="relative flex flex-col gap-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500/20 text-brand-100 overflow-hidden">
                        {school.iconUrl ? (
                          <img src={school.iconUrl} alt={school.name} className="h-full w-full object-cover" />
                        ) : (
                          <Building2 className="h-5 w-5" />
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-white">{school.name}</h3>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {school.prefecture ? (
                    <div className="flex items-center gap-2 text-sm text-white/70">
                      <MapPin className="h-4 w-4 text-brand-300" />
                      <span>{school.prefecture}</span>
                    </div>
                  ) : null}

                  {school.league ? (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{school.league}</Badge>
                    </div>
                  ) : null}

                  {school.coachesCount > 0 ? (
                    <div className="flex items-center gap-2 text-sm text-white/70">
                      <Users2 className="h-4 w-4 text-brand-300" />
                      <span>コーチ {school.coachesCount}名</span>
                    </div>
                  ) : null}

                  {school.selfIntroduction ? (
                    <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                      <p className="line-clamp-3 text-sm text-white/70">{school.selfIntroduction}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  )
}
